// frontend/src/pages/supplierCatalogSave.js
//
// The Supplier catalog grid's Save: the edited cells of one supplier's rows,
// written in the order the item's cost of record needs.
//
// Two kinds of cell are queued. A term (price, MOQ, supplier SKU, …) goes to
// bulk-update, one request for every edited row. A tick of the Default box
// elects that relation the item's default supplier through set-default, one
// request per row: the backend keeps promotion out of bulk-update because an
// election re-pegs the item's cost of record (Model B) and may need the
// merchant's adopt confirmation. Terms go FIRST. A price typed on a
// not-yet-default relation moves no cost, and the election that follows
// re-pegs the cost once, behind one confirmation; the other order would move
// it twice. A tick on a row whose terms the server refused does not run: the
// election would peg the cost to the price the server still holds, not the
// one the merchant typed.
//
// Pure over the dirty-cell map, the two writes and the gate, so the order
// and the per-row outcomes are table-testable without a grid.

import { dirtyCellPayload } from '../utils/dirtyCellPayload';

export const DEFAULT_FIELD = 'is_default';

/** True for the dirty-cell entry of a Default tick. */
export const isDefaultTick = (info) => info.fieldName === DEFAULT_FIELD;

/**
 * @param {Map<string, {rowId, fieldName, rowData}>} cells
 *   useEditableGridState's dirty-cell map (`getChangedCellsRef()`).
 * @param {object} deps
 * @param {(payload) => Promise<{results, summary}>} deps.bulkUpdate
 * @param {({id, confirm?}) => Promise<object>} deps.setDefault
 * @param {(opts) => Promise<boolean>} deps.requestGate
 *   useResolutionGate's `request`: true once confirmed and written, false
 *   when the merchant cancels.
 * @param {(err: Error) => string} deps.refusedMessage
 *   The merchant's sentence for a promotion the server refused.
 * @param {string} deps.stillGatedMessage
 *   The sentence for a retry that came back still gated, which is not a save.
 * @returns {Promise<{
 *   savedTerms: Set<string>,   row ids whose terms were written
 *   savedTicks: Set<string>,   row ids now the item's default
 *   failed: Array<{id, success: false, error?: string}>,
 * }>}  A cancelled tick is in none of them: it stays queued.
 */
export async function saveSupplierCatalogEdits(cells, {
  bulkUpdate, setDefault, requestGate, refusedMessage, stillGatedMessage,
}) {
  const terms = new Map([...cells].filter(([, info]) => !isDefaultTick(info)));
  const ticks = [...cells.values()].filter(isDefaultTick);
  const savedTerms = new Set();
  const savedTicks = new Set();
  const failed = [];

  if (terms.size > 0) {
    const data = await bulkUpdate(dirtyCellPayload(terms));
    for (const row of data.results) {
      if (row.success) savedTerms.add(String(row.id));
      else failed.push(row);
    }
  }
  const termsRefused = new Set(failed.map((row) => String(row.id)));

  // One at a time: each election may open the adopt dialog, and the gate
  // hosts one question at a time.
  for (const { rowId } of ticks) {
    const id = String(rowId);
    if (termsRefused.has(id)) continue;
    try {
      const data = await setDefault({ id: rowId });
      let written = true;
      if (data.requires_confirmation) {
        written = await requestGate({
          changeContext: data.change_context,
          affectedItems: data.affected_items,
          onConfirm: async (confirmBody) => {
            try {
              const retry = await setDefault({ id: rowId, confirm: confirmBody });
              if (retry.requires_confirmation) return { error: stillGatedMessage };
            } catch (err) {
              return { error: refusedMessage(err) };
            }
          },
        });
      }
      if (written) savedTicks.add(id);
    } catch (err) {
      failed.push({ id: rowId, success: false, error: refusedMessage(err) });
    }
  }

  return { savedTerms, savedTicks, failed };
}
